import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {number} Invalidates pagination work started before a filter update. */
  #paginationRevision = 0;
  #filterUpdatePending = false;
  #pageRequestController = new AbortController();
  #prefetchTimer;

  /** @type {MutationObserver | undefined} */
  #filterUpdateObserver;

  #isRenderingNextPage = false;
  #isRenderingPreviousPage = false;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#prefetchAdjacentPages();
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#paginationRevision++;
    this.#cancelPageRequests();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
      this.infinityScrollObserver = undefined;
    }
    this.#filterUpdateObserver?.disconnect();
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    if (this.#filterUpdatePending) return;
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (this.#filterUpdatePending) return;
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  #cancelPageRequests() {
    clearTimeout(this.#prefetchTimer);
    this.#prefetchTimer = undefined;
    this.#pageRequestController.abort();
    this.#pageRequestController = new AbortController();
  }

  #prefetchAdjacentPages() {
    if (!this.hasAttribute('data-immediate-filter-updates')) {
      this.#fetchPage('next');
      this.#fetchPage('previous');
      return;
    }
    clearTimeout(this.#prefetchTimer);
    this.#prefetchTimer = setTimeout(() => {
      this.#prefetchTimer = undefined;
      if (!this.isConnected || this.#filterUpdatePending) return;
      this.#fetchPage('next');
      this.#fetchPage('previous');
    }, 250);
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (this.#filterUpdatePending) return false;
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const revision = this.#paginationRevision;
    const resolvePromise = type === 'next' ? this.#resolveNextPagePromise : this.#resolvePreviousPagePromise;
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const finish = () => {
      resolvePromise?.();
      if (type === 'next' && this.#resolveNextPagePromise === resolvePromise) {
        this.#resolveNextPagePromise = null;
      } else if (type === 'previous' && this.#resolvePreviousPagePromise === resolvePromise) {
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      finish();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url, revision);
    } catch {
      return;
    } finally {
      finish();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined, revision = this.#paginationRevision) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url, {
      signal: this.#pageRequestController.signal,
      priority: 'low',
    });
    if (!this.isConnected || revision !== this.#paginationRevision) return;
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    if (this.#filterUpdatePending || this.#isRenderingNextPage) return;
    this.#isRenderingNextPage = true;
    try {
      const revision = this.#paginationRevision;
      const { grid } = this.refs;

      if (!grid) return;

      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return;
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        if (!this.isConnected || revision !== this.#paginationRevision) return;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      if (!this.isConnected || revision !== this.#paginationRevision || grid !== this.refs.grid || !grid.isConnected) return;
      const lastPage = Number(grid.lastElementChild?.dataset.page);
      if (Number.isFinite(lastPage) && nextPage.page <= lastPage) return;

      const fragment = document.createDocumentFragment();
      fragment.append(...nextPageItemElements);
      grid.append(fragment);
      sectionRenderer.refreshWishlistButtons(this.sectionId);

      this.#aspectRatioHelper.processNewElements();

      history.replaceState(history.state, '', nextPage.url.toString());

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } finally {
      this.#isRenderingNextPage = false;
    }
  }

  async #renderPreviousPage() {
    if (this.#filterUpdatePending || this.#isRenderingPreviousPage) return;
    this.#isRenderingPreviousPage = true;
    try {
      const revision = this.#paginationRevision;
      const { grid } = this.refs;

      if (!grid) return;

      const previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage)) return;

      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;
        if (!this.isConnected || revision !== this.#paginationRevision) return;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      if (!this.isConnected || revision !== this.#paginationRevision || grid !== this.refs.grid || !grid.isConnected) return;
      const firstPage = Number(grid.firstElementChild?.dataset.page);
      if (Number.isFinite(firstPage) && previousPage.page >= firstPage) return;

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // Insert the prior page as one ordered DOM operation.
      const fragment = document.createDocumentFragment();
      fragment.append(...previousPageItemElements);
      grid.insertBefore(fragment, grid.firstChild);
      sectionRenderer.refreshWishlistButtons(this.sectionId);

      this.#aspectRatioHelper.processNewElements();

      history.replaceState(history.state, '', previousPage.url.toString());

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } finally {
      this.#isRenderingPreviousPage = false;
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  setFilterUpdatePending(pending) {
    this.#filterUpdatePending = pending;
    if (pending) {
      this.#paginationRevision++;
      this.#cancelPageRequests();
      this.infinityScrollObserver?.disconnect();
      this.infinityScrollObserver = undefined;
      this.#filterUpdateObserver?.disconnect();
      this.#filterUpdateObserver = undefined;
    } else if (this.isConnected) {
      this.#observeViewMore();
      this.#prefetchAdjacentPages();
    }
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#paginationRevision++;
    this.#cancelPageRequests();
    this.pages.clear();

    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
      this.infinityScrollObserver = undefined;
    }
    this.#filterUpdateObserver?.disconnect();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Compare the rendered cards so filtering is detected even when page count is unchanged.
    const currentGridHTML = this.refs.grid?.innerHTML;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      const newGridHTML = this.refs.grid?.innerHTML;
      if (newGridHTML !== undefined && newGridHTML !== currentGridHTML) {
        observer.disconnect();
        this.#filterUpdateObserver = undefined;

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#prefetchAdjacentPages();
      }
    });
    this.#filterUpdateObserver = observer;

    if (this.isConnected) {
      observer.observe(this, { childList: true, subtree: true });

      // Recover pagination if the rendered grid does not change after the filter update.
      setTimeout(() => {
        if (this.#filterUpdateObserver === observer) {
          observer.disconnect();
          this.#filterUpdateObserver = undefined;
          if (this.isConnected) {
            this.#observeViewMore();
            this.#prefetchAdjacentPages();
          }
        }
      }, 3000);
    }
  };
}
